import { useNavigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';

// Asset URLs — Pokémon Let's Go style, served locally from /map-assets/
// to ensure the white backgrounds are stripped (PNG with alpha channel).
const ASSETS = {
  map_background: 'https://static.prod-images.emergentagent.com/jobs/3455129a-0603-448b-a0bd-7103fb4237a6/images/7573483110b17bb81e6a699bc7d97baef1d3ba46b7ecbdfe0e7cf5606f573942.png',
  tarefas:     '/map-assets/task.png',
  pesquisa:    '/map-assets/research.png',
  compras:     '/map-assets/shop.png',
  crescimento: '/map-assets/growth.png',
  jornadas:    '/map-assets/journey.png',
};

const BUILDINGS = [
  { key: 'tarefas',     label: 'Centro de Tarefas',     path: '/student/missions', img: ASSETS.tarefas,     speech: 'Entrar no Centro de Tarefas?' },
  { key: 'jornadas',    label: 'Centro das Jornadas',   path: '/student/journeys', img: ASSETS.jornadas,    speech: 'Embarcar em uma Jornada?' },
  { key: 'pesquisa',    label: 'Centro de Pesquisa',    path: '/student/lab',      img: ASSETS.pesquisa,    speech: 'Investigar no Laboratório?' },
  { key: 'compras',     label: 'Centro de Compras',     path: '/student/shop',     img: ASSETS.compras,     speech: 'Visitar o Centro de Compras?' },
  { key: 'crescimento', label: 'Centro de Crescimento', path: '/student/history',  img: ASSETS.crescimento, speech: 'Acompanhar seu Crescimento?' },
];

const BuildingIcon = ({ b, active, onClick, swayDelay = 1 }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`map-building-${b.key}`}
    aria-label={b.label}
    className={`group relative shrink-0 snap-center w-20 sm:w-24 md:w-28 transition-transform duration-300 hover:-translate-y-1.5 ${active ? 'scale-105' : 'hover:scale-105'} focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-300/70 rounded-2xl`}
  >
    {/* Building illustration */}
    <div className={`relative ${active ? 'animate-pulse-soft' : `animate-sway animate-sway-d${swayDelay}`}`}>
      <img
        src={b.img}
        alt={b.label}
        className="w-full h-auto drop-shadow-[2px_3px_0_rgba(15,23,42,0.45)] select-none pointer-events-none"
        draggable={false}
        loading="lazy"
      />
      {active && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-2 rounded-full bg-amber-400/80 blur-sm" />
      )}
    </div>

    {/* Label badge */}
    <div className="mt-0.5 text-center">
      <span className="inline-block font-outfit font-black text-[9px] sm:text-[10px] tracking-[0.12em] uppercase text-white bg-slate-900/85 px-2 py-0.5 rounded-full border-2 border-amber-300 shadow-md leading-tight">
        {b.label}
      </span>
    </div>

    {/* Speech bubble — positioned BELOW the building so it never gets cut by the top of the screen */}
    <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+12px)] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
      <div className="relative bg-white text-slate-900 font-outfit font-bold text-xs px-3 py-1.5 rounded-full shadow-xl border-2 border-slate-900 whitespace-nowrap">
        <span className="absolute left-1/2 -translate-x-1/2 -top-[6px] w-2.5 h-2.5 bg-white border-l-2 border-t-2 border-slate-900 rotate-45" />
        {b.speech}
      </div>
    </div>
  </button>
);

/**
 * Top map navigation that replaces the traditional navbar on student pages.
 * - Desktop: buildings centered with even spacing.
 * - Mobile: horizontal snap-scroll carousel.
 */
export const PokeMapNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeKey = useMemo(() => {
    const b = BUILDINGS.find((x) => location.pathname.startsWith(x.path));
    return b?.key || null;
  }, [location.pathname]);

  const handleNav = (path) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  return (
    <div
      data-testid="pokemap-nav"
      className="relative w-full border-b-4 border-amber-700/70 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.65)] z-30"
    >
      {/* Map background (kept clipped via its own container so the speech
          bubbles below the buildings are NOT cut off by overflow-hidden). */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.map_background})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sky-200/30 via-transparent to-emerald-900/25" />
      </div>

      {/* Buildings layout — overflow VISIBLE so speech bubbles can extend below */}
      <div className="relative h-32 sm:h-40 md:h-44 flex items-end justify-start md:justify-center gap-3 sm:gap-6 md:gap-10 px-4 sm:px-8 pb-3 pt-10 overflow-x-auto md:overflow-visible md:overflow-y-visible snap-x snap-mandatory scrollbar-hide">
        <div className="hidden md:block w-28 shrink-0" aria-hidden="true" /> {/* spacer to keep buildings clear of the trainer card */}
        {BUILDINGS.map((b, idx) => (
          <BuildingIcon
            key={b.key}
            b={b}
            active={activeKey === b.key}
            onClick={() => handleNav(b.path)}
            swayDelay={(idx % 4) + 1}
          />
        ))}
      </div>
    </div>
  );
};

export default PokeMapNav;
